/**
 * Screen — VALUATION HISTORY (`/valuation/history`).
 *
 * VAL-HISTORY-2, owner ruling 09-24: every engagement this login can see,
 * unsigned or signed, most-recently-edited first, with a search box. Opens
 * through the same routing as Dashboard (_EngagementTable's action chip, via
 * nextStep) — no new edit/version logic here.
 */
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import { listValuationEngagements } from '@/api/valuation'
import { formatApiError } from '@/utils/httpErrors'
import type { ValuationEngagement } from '@/types/valuation'
import { _EngagementTable } from './_EngagementTable'

export default function ValuationHistory() {
  const [engagements, setEngagements] = useState<ValuationEngagement[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  useEffect(() => {
    let live = true
    listValuationEngagements()
      .then(res => { if (live) setEngagements(res) })
      .catch((err: unknown) => { if (live) setLoadError(formatApiError(err)) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [])

  const sorted = [...engagements].sort((a, b) => b.updated_at.localeCompare(a.updated_at))
  const filtered = search.trim()
    ? sorted.filter(en => en.subject_company_name.toLowerCase().includes(search.trim().toLowerCase()))
    : sorted

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-7xl p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {loadError}
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">History</h1>
        <p className="mt-1 text-sm text-gray-500">Every engagement you have access to, most recently edited first. Open one to keep working.</p>
      </div>

      {engagements.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <p className="text-sm font-medium text-gray-900">No engagements yet.</p>
            <Link to="/valuation/new" className="inline-block">
              <Button type="button">Start your first valuation</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <>
          <Input
            type="search"
            placeholder="Search by company name"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="max-w-sm"
          />

          {filtered.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center">
                <p className="text-sm font-medium text-gray-900">No engagements match.</p>
              </CardContent>
            </Card>
          ) : (
            <_EngagementTable engagements={filtered} showLastEdited showVersions />
          )}
        </>
      )}
    </div>
  )
}
